import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { 
  XMarkIcon, 
  CloudArrowUpIcon,
  DocumentTextIcon,
  UserIcon,
  TagIcon,
  CheckCircleIcon,
  LightBulbIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
// LexicalDocumentEditor removed - using simple textarea for inline editing
import { EnhancedDocumentMetadataForm } from '../../EnhancedUploadForm';
import { FileUploadArea } from '../../FileUploadArea';
import { useKeycloak } from '@react-keycloak/web';
import {
  useDocumentAPI,
  type EdgeCiteAnalysisStatusResponse,
} from '../../../DocumentAPI';
import { useNavigate } from 'react-router-dom';
import {
  PENDING_UPLOAD_DEEP_SEARCH_KEY,
  buildDefaultUploadedDocumentPrompt,
  type PendingUploadDeepSearchHandoff,
} from '@/features/dms/uploadHandoff';
import {
  getMatterScopedUploadMissingFields,
  isMatterScopedUploadReady,
} from '../../../uploadValidation';

const EMPTY_INITIAL_FILES: File[] = [];

interface AddDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  realmId: string;
  matterId?: string;
  clientId?: string;
  initialFiles?: File[];
  initialTitle?: string;
  initialDescription?: string;
  initialClientName?: string;
  initialMatterName?: string;
  initialMatterNumber?: string;
}

import { EnhancedDocumentFormData } from '../../../types/DocumentTypes';

// AI Suggestions Interface
interface AISuggestions {
  title: string;
  description: string;
  organization?: DocumentOrganizationSuggestion;
  confidence: {
    title: number;
    description: number;
  };
  processingTime?: number;
}

interface DocumentOrganizationSuggestion {
  primaryType: string;
  subType?: string;
  phase: string;
  categoryLabel: string;
  filingStatus: string;
  sourceRole: string;
  documentDate?: string;
  filedDate?: string;
  servedDate?: string;
  receivedDate?: string;
  confidence: number;
  method: string;
  tags: string[];
  rationale: string;
}

type EdgeCiteDocumentAnalysisState = 'skipped' | 'queued' | 'processing' | 'completed' | 'timed_out' | 'blocked' | 'failed';

interface CreatedDocumentAnalysis {
  documentId: string;
  versionId: string;
  title: string;
  analysisStatus: EdgeCiteDocumentAnalysisState;
  analysisMessage?: string;
  analysisProgress: number;
  jobId?: string | null;
  chunkCount?: number | null;
  citationCount?: number | null;
  clauseCount?: number | null;
  partyCount?: number | null;
  embeddingModel?: string | null;
  collectionName?: string | null;
  summaryText?: string | null;
  feed?: EdgeCiteAnalysisStatusResponse['feed'];
  errorMessage?: string | null;
}

const isActiveEdgeCiteStatus = (status: EdgeCiteAnalysisStatusResponse['status']): boolean =>
  status === 'queued' || status === 'processing';

const extractEdgeCiteMetric = (status: EdgeCiteAnalysisStatusResponse, keys: string[]): number | null => {
  const candidates = [
    status.insight?.data,
    status.document?.edgecite_weaviate_analysis,
    ...status.steps.map((step) => step.results),
  ];

  for (const source of candidates) {
    if (!source || typeof source !== 'object') continue;
    for (const key of keys) {
      const value = (source as Record<string, unknown>)[key];
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
        return Number(value);
      }
    }
  }

  return null;
};

const summarizeEdgeCiteStatus = (
  status: EdgeCiteAnalysisStatusResponse,
  fallbackTitle: string,
): CreatedDocumentAnalysis => {
  const message = status.message || status.insight?.summary_text || `EdgeCite is processing "${fallbackTitle}".`;
  const failedStep = status.steps.find((step) => step.status === 'failed');

  return {
    documentId: status.document_id,
    versionId: status.version_id || '',
    title: status.document?.title || fallbackTitle,
    analysisStatus: status.status === 'completed'
      ? 'completed'
      : status.status === 'failed'
        ? 'failed'
        : status.status === 'queued'
          ? 'queued'
          : status.status === 'processing'
            ? 'processing'
            : 'skipped',
    analysisMessage: failedStep?.error_message || failedStep?.message || message,
    analysisProgress: Math.max(0, Math.min(100, status.progress || 0)),
    jobId: status.job_id,
    chunkCount: extractEdgeCiteMetric(status, ['chunk_count', 'chunks', 'total_chunks']),
    citationCount: extractEdgeCiteMetric(status, ['citation_count', 'citations_count', 'citations']),
    clauseCount: extractEdgeCiteMetric(status, ['clause_count', 'clauses_count', 'legal_clause_count']),
    partyCount: extractEdgeCiteMetric(status, ['party_count', 'parties_count']),
    embeddingModel: typeof status.insight?.data?.embedding_model === 'string'
      ? status.insight.data.embedding_model
      : typeof status.document?.edgecite_weaviate_analysis?.embedding_model === 'string'
        ? status.document.edgecite_weaviate_analysis.embedding_model
        : null,
    collectionName: typeof status.insight?.data?.collection_name === 'string'
      ? status.insight.data.collection_name
      : typeof status.document?.edgecite_weaviate_analysis?.collection_name === 'string'
        ? status.document.edgecite_weaviate_analysis.collection_name
        : null,
    summaryText: status.insight?.summary_text || null,
    feed: status.feed,
    errorMessage: failedStep?.error_message || null,
  };
};

const resolveEdgeCiteAnalysisTimeoutMs = (file?: File): number => {
  if (!file) return 180_000;
  const lowerName = file.name.toLowerCase();
  const isPdf = file.type === 'application/pdf' || lowerName.endsWith('.pdf');
  const isOffice = /\.(docx?|xlsx?|pptx?)$/.test(lowerName);
  const sizeMb = file.size / (1024 * 1024);

  if (sizeMb > 35 || isPdf) return 300_000;
  if (sizeMb > 15 || isOffice) return 240_000;
  return 180_000;
};

const AddDocumentModal: React.FC<AddDocumentModalProps> = ({
  isOpen,
  onClose,
  realmId,
  matterId,
  clientId,
  initialFiles = EMPTY_INITIAL_FILES,
  initialTitle = '',
  initialDescription = '',
  initialClientName = '',
  initialMatterName = '',
  initialMatterNumber = '',
}) => {
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const documentAPI = useDocumentAPI();
  const [currentUser, setCurrentUser] = useState<{ id: string; name: string } | null>(null);

  // Get current user info from Keycloak token
  useEffect(() => {
    if (keycloak.token) {
      try {
        const payload = JSON.parse(atob(keycloak.token.split('.')[1]));
        setCurrentUser({
          id: payload.sub || '',
          name: payload.name || payload.preferred_username || payload.email || 'Unknown User'
        });
      } catch (error) {
        console.warn('Could not parse user info from token:', error);
        setCurrentUser({ id: '', name: 'Unknown User' });
      }
    }
  }, [keycloak.token]);

  const [formData, setFormData] = useState<EnhancedDocumentFormData>({
    title: initialTitle,
    description: initialDescription,
    clientId: clientId || '',
    matterId: matterId || '',
    categoryId: '',
    version: '1.0',
    versionNotes: '',
    tags: [],
    confidentiality: 'internal',
    documentOwner: '',
    retentionPolicy: '',
    relatedDocuments: [],
    aiProcessing: true,
    customVersionLabel: '',
    // Enhanced fields
    jurisdiction: '',
    lifecycleStage: 'draft',
    documentPurpose: '',
    responsibleAttorney: '',
    notifyOnChange: false,
    effectiveDate: '',
    expiryDate: '',
    reminderEnabled: false,
    reminderDays: 7,
    aiProcessingProfile: 'default',
    aiOnlyVisibility: false,
    aiConfidencePreview: 0,
    aiSuggestedRelations: [],
    uploadedFiles: initialFiles,
    metadataCloneEnabled: false,
    legalDocumentType: '',
    legalDocumentSubtype: '',
    documentPhase: '',
    organizationCategoryLabel: '',
    organizationConfidence: 0,
    organizationRationale: '',
    filingStatus: '',
    sourceRole: '',
    documentDate: '',
    filedDate: '',
    servedDate: '',
    receivedDate: '',
  });

  const [uploadedFiles, setUploadedFiles] = useState<File[]>(initialFiles);
  const [loading, setLoading] = useState(false);
  
  // AI Suggestions state
  const [aiSuggestions, setAiSuggestions] = useState<AISuggestions | null>(null);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiPrefillNotice, setAiPrefillNotice] = useState<string | null>(null);
  const [renderingGuarantee, setRenderingGuarantee] = useState<string | null>(null);
  const [ingestStatus, setIngestStatus] = useState<{
    phase: 'idle' | 'uploading' | 'creating' | 'analyzing' | 'ready' | 'error';
    message: string;
    detail?: string | null;
  }>({
    phase: 'idle',
    message: 'Upload a document to start ingestion.',
    detail: null,
  });
  const [createdDocuments, setCreatedDocuments] = useState<CreatedDocumentAnalysis[]>([]);

  const canRunAiPrefill = uploadedFiles.length === 1;
  const canSubmitMatterScopedUpload = isMatterScopedUploadReady({
    fileCount: uploadedFiles.length,
    title: formData.title,
    clientId: formData.clientId,
    matterId: formData.matterId,
  });
  const latestCreatedDocument = createdDocuments[0] || null;
  // The "Ask AI About This Document" CTA is gated on EdgeCite/Weaviate
  // completion. A DMS version row alone is not enough; the document must be
  // chunked, embedded, and analyzed by EdgeCite first.
  const canLaunchAi =
    createdDocuments.length === 1 &&
    !!formData.matterId &&
    latestCreatedDocument?.analysisStatus === 'completed';

  const setIdleStatus = useCallback(() => {
    setIngestStatus({
      phase: 'idle',
      message: 'Upload a document to start ingestion.',
      detail: null,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    setFormData(prev => ({
      ...prev,
      title: initialTitle,
      description: initialDescription,
      clientId: clientId || '',
      matterId: matterId || '',
      uploadedFiles: initialFiles,
      legalDocumentType: '',
      legalDocumentSubtype: '',
      documentPhase: '',
      organizationCategoryLabel: '',
      organizationConfidence: 0,
      organizationRationale: '',
      filingStatus: '',
      sourceRole: '',
      documentDate: '',
      filedDate: '',
      servedDate: '',
      receivedDate: '',
    }));
    setUploadedFiles(initialFiles);
    setCreatedDocuments([]);
    setAiSuggestions(null);
    setAiPrefillNotice(null);
    setRenderingGuarantee(null);
    setIngestStatus({
      phase: 'idle',
      message: initialFiles.length > 0
        ? `${initialFiles.length} file${initialFiles.length === 1 ? '' : 's'} selected.`
        : 'Upload a document to start ingestion.',
      detail: initialFiles.length > 0
        ? 'Use AI Fill to draft the title and description, then submit to ingest and index the document.'
        : null,
    });
  }, [isOpen, initialFiles, initialTitle, initialDescription, clientId, matterId]);

  const wait = useCallback((ms: number) => new Promise((resolve) => setTimeout(resolve, ms)), []);

  const stageLabel = useMemo(() => {
    switch (ingestStatus.phase) {
      case 'uploading':
        return 'Uploading';
      case 'creating':
        return 'Creating';
      case 'analyzing':
        return 'EdgeCite';
      case 'ready':
        return 'Ready';
      case 'error':
        return 'Needs Review';
      case 'idle':
      default:
        return 'Waiting';
    }
  }, [ingestStatus.phase]);

  const updateCreatedDocument = useCallback((documentId: string, patch: Partial<CreatedDocumentAnalysis>) => {
    setCreatedDocuments((current) =>
      current.map((doc) => (doc.documentId === documentId ? { ...doc, ...patch } : doc)),
    );
  }, []);

  const runEdgeCiteUntilReady = useCallback(async (
    documentId: string,
    versionId: string,
    title: string,
    sourceFile?: File,
    options: { forceNewJob?: boolean } = {},
  ): Promise<CreatedDocumentAnalysis> => {
    let jobId: string | null = null;

    if (!options.forceNewJob) {
      try {
        const existingStatus = await documentAPI.getEdgeCiteDocumentAnalysisStatus(documentId, { versionId });
        const existingSummary = summarizeEdgeCiteStatus(existingStatus, title);
        if (existingStatus.status === 'completed') {
          updateCreatedDocument(documentId, existingSummary);
          setIngestStatus({
            phase: 'ready',
            message: `EdgeCite analysis is already complete for "${existingSummary.title}".`,
            detail: existingSummary.summaryText || existingSummary.analysisMessage || null,
          });
          return existingSummary;
        }

        if (isActiveEdgeCiteStatus(existingStatus.status) && existingStatus.job_id) {
          jobId = existingStatus.job_id;
          updateCreatedDocument(documentId, existingSummary);
        }
      } catch {
        // Status may not exist yet for a freshly uploaded document. Queue below.
      }
    }

    if (!jobId) {
      const queued = await documentAPI.triggerEdgeCiteDocumentAnalysis(documentId, { versionId });
      jobId = queued.job_id;
      updateCreatedDocument(documentId, {
        analysisStatus: 'queued',
        analysisProgress: 2,
        analysisMessage: queued.message || 'EdgeCite analysis queued.',
        jobId,
      });
    }

    const startedAt = Date.now();
    const timeoutMs = resolveEdgeCiteAnalysisTimeoutMs(sourceFile);

    while (Date.now() - startedAt < timeoutMs) {
      const status = await documentAPI.getEdgeCiteDocumentAnalysisStatus(documentId, { versionId, jobId: jobId || undefined });
      const summary = summarizeEdgeCiteStatus(status, title);
      updateCreatedDocument(documentId, summary);

      setIngestStatus({
        phase: status.status === 'completed' ? 'ready' : 'analyzing',
        message: status.status === 'completed'
          ? `EdgeCite analysis complete for "${summary.title}".`
          : `EdgeCite is analyzing "${summary.title}".`,
        detail: [
          summary.analysisMessage,
          summary.chunkCount != null ? `${summary.chunkCount} chunks` : null,
          summary.citationCount != null ? `${summary.citationCount} citations` : null,
        ].filter(Boolean).join(' · ') || null,
      });

      if (status.status === 'completed' || status.status === 'failed') {
        return summary;
      }

      await wait(2_000);
    }

    const timedOut: CreatedDocumentAnalysis = {
      documentId,
      versionId,
      title,
      analysisStatus: 'timed_out',
      analysisProgress: 99,
      analysisMessage: 'EdgeCite is still processing. Use retry or the row sparkles status to continue watching this analysis.',
      jobId,
    };
    updateCreatedDocument(documentId, timedOut);
    return timedOut;
  }, [documentAPI, updateCreatedDocument, wait]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const missingFields = getMatterScopedUploadMissingFields({
      fileCount: uploadedFiles.length,
      title: formData.title,
      clientId: formData.clientId,
      matterId: formData.matterId,
    });

    if (missingFields.length > 0) {
      alert(`Complete required fields before upload: ${missingFields.join(', ')}.`);
      return;
    }
    
    setLoading(true);
    setCreatedDocuments([]);
    setAiPrefillNotice(null);
    setRenderingGuarantee(null);
    
    try {
      const created: CreatedDocumentAnalysis[] = [];

      for (const file of uploadedFiles) {
        setIngestStatus({
          phase: 'uploading',
          message: `Ingesting "${file.name}"...`,
          detail: null,
        });

        setIngestStatus({
          phase: 'creating',
          message: `Creating the DMS record for "${file.name}" from the original uploaded bytes...`,
          detail: null,
        });

        const ingestResponse = await documentAPI.ingestDocumentUpload(file, {
          intent: 'ingest',
          title: uploadedFiles.length === 1 ? formData.title : `${formData.title || 'Untitled'} - ${file.name}`,
          description: formData.description,
          clientId: formData.clientId,
          matterId: formData.matterId,
          categoryId: formData.categoryId,
          categoryLabel: formData.organizationCategoryLabel,
          primaryType: formData.legalDocumentType,
          subType: formData.legalDocumentSubtype,
          documentPhase: formData.documentPhase,
          filingStatus: formData.filingStatus,
          sourceRole: formData.sourceRole,
          documentDate: formData.documentDate,
          filedDate: formData.filedDate,
          servedDate: formData.servedDate,
          receivedDate: formData.receivedDate,
          tags: formData.tags,
          version: formData.version || '1.0',
        });

        if (ingestResponse.intent !== 'ingest') {
          throw new Error(`Document "${file.name}" did not complete ingest.`);
        }
        const newDocument = ingestResponse.document;
        const createdVersionId = ingestResponse.document.versionId;
        setRenderingGuarantee(ingestResponse.rendering.previewMessage);

        setIngestStatus({
          phase: 'analyzing',
          message: `Starting canonical chunking and embedding for "${newDocument.title}"...`,
          detail: formData.matterId
            ? 'EdgeCite will chunk, embed, classify, extract citations, and publish Weaviate retrieval context.'
            : 'EdgeCite will still analyze the document. Assign a matter before launching matter-scoped AI chat.',
        });

        const initialCreatedDoc: CreatedDocumentAnalysis = {
          documentId: newDocument.documentId,
          versionId: createdVersionId,
          title: newDocument.title || file.name,
          analysisStatus: 'queued',
          analysisMessage: 'EdgeCite analysis queued.',
          analysisProgress: 0,
          jobId: null,
        };
        created.push(initialCreatedDoc);
        setCreatedDocuments([...created]);

        const edgeCiteResult = await runEdgeCiteUntilReady(
          newDocument.documentId,
          createdVersionId,
          newDocument.title || file.name,
          file,
        );

        Object.assign(initialCreatedDoc, edgeCiteResult);
        setCreatedDocuments([...created]);
      }

      setCreatedDocuments(created);
      const single = created[0];
      const allReady = created.every((doc) => doc.analysisStatus === 'completed');
      setIngestStatus({
        phase: allReady ? 'ready' : 'idle',
        message: created.length === 1
          ? `Created "${single.title}".`
          : `Created ${created.length} documents.`,
        detail: created.length === 1
          ? single.analysisMessage
          : 'Review each document for EdgeCite analysis readiness below.',
      });
    } catch (error) {
      setIngestStatus({
        phase: 'error',
        message: 'Document ingest failed.',
        detail: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof EnhancedDocumentFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (files: File[]) => {
    setUploadedFiles(files);
    setFormData(prev => ({ ...prev, uploadedFiles: files }));
    setCreatedDocuments([]);
    setAiPrefillNotice(null);
    setRenderingGuarantee(null);
    if (files.length === 0) {
      setIdleStatus();
      return;
    }
    setIngestStatus({
      phase: 'idle',
      message: `${files.length} file${files.length === 1 ? '' : 's'} selected.`,
      detail: 'Use AI Fill to draft the title and description, then submit to ingest and index the document.',
    });
  };

  const applyOrganizationSuggestion = useCallback((
    current: EnhancedDocumentFormData,
    organization?: DocumentOrganizationSuggestion,
  ): EnhancedDocumentFormData => {
    if (!organization) return current;
    return {
      ...current,
      legalDocumentType: organization.primaryType || current.legalDocumentType,
      legalDocumentSubtype: organization.subType || current.legalDocumentSubtype,
      documentPhase: organization.phase || current.documentPhase,
      organizationCategoryLabel: organization.categoryLabel || current.organizationCategoryLabel,
      organizationConfidence: Math.round((organization.confidence || 0) * 100),
      organizationRationale: organization.rationale || current.organizationRationale,
      filingStatus: (organization.filingStatus || current.filingStatus || '') as EnhancedDocumentFormData['filingStatus'],
      sourceRole: (organization.sourceRole || current.sourceRole || '') as EnhancedDocumentFormData['sourceRole'],
      documentDate: organization.documentDate || current.documentDate,
      filedDate: organization.filedDate || current.filedDate,
      servedDate: organization.servedDate || current.servedDate,
      receivedDate: organization.receivedDate || current.receivedDate,
      tags: [...new Set([
        ...(current.tags || []),
        ...(organization.tags || []),
      ])],
    };
  }, []);

  // AI Processing function
  const processWithAI = useCallback(async () => {
    if (uploadedFiles.length !== 1) {
      setAiPrefillNotice('AI Fill works on a single uploaded document at a time.');
      return;
    }

    setAiProcessing(true);
    setAiPrefillNotice(null);
    try {
      const result = await documentAPI.ingestDocumentUpload(uploadedFiles[0], {
        intent: 'analyze',
        clientId: formData.clientId,
        matterId: formData.matterId,
      });
      const suggestions = result.suggestions as AISuggestions;
      setAiSuggestions(suggestions);
      setFormData(prev => ({
        ...applyOrganizationSuggestion(prev, suggestions.organization),
        title: suggestions.title || prev.title,
        description: suggestions.description || prev.description,
      }));
      setRenderingGuarantee(result.rendering.previewMessage);
      setAiPrefillNotice(
        result.duplicate.isDuplicate
          ? `${result.duplicate.message} The original content match was detected by SHA-256 before ingest.`
          : suggestions.organization
            ? `AI filled the title, description, and organization as ${suggestions.organization.categoryLabel}.`
            : 'AI filled the title and description from the uploaded document.',
      );
    } catch (error) {
      setAiPrefillNotice(error instanceof Error ? error.message : 'AI Fill failed.');
    } finally {
      setAiProcessing(false);
    }
  }, [applyOrganizationSuggestion, documentAPI, formData.clientId, formData.matterId, uploadedFiles]);

  // Apply AI suggestions
  const applyAiSuggestions = () => {
    if (aiSuggestions) {
      setFormData(prev => ({
        ...prev,
        ...applyOrganizationSuggestion(prev, aiSuggestions.organization),
        title: aiSuggestions.title,
        description: aiSuggestions.description,
      }));
      setAiPrefillNotice('AI suggestions applied to the form.');
    }
  };

  const handleRerunEdgeCiteAnalysis = useCallback(async (doc: CreatedDocumentAnalysis) => {
    setLoading(true);
    setIngestStatus({
      phase: 'analyzing',
      message: `Restarting EdgeCite analysis for "${doc.title}"...`,
      detail: 'EdgeCite will refresh chunks, embeddings, extracted citations, and document intelligence.',
    });
    updateCreatedDocument(doc.documentId, {
      analysisStatus: 'queued',
      analysisProgress: 0,
      analysisMessage: 'Restarting EdgeCite analysis.',
      errorMessage: null,
    });

    try {
      const result = await runEdgeCiteUntilReady(doc.documentId, doc.versionId, doc.title, undefined, { forceNewJob: true });
      updateCreatedDocument(doc.documentId, result);
      setIngestStatus({
        phase: result.analysisStatus === 'completed' ? 'ready' : result.analysisStatus === 'failed' ? 'error' : 'idle',
        message: result.analysisStatus === 'completed'
          ? `EdgeCite analysis complete for "${result.title}".`
          : `EdgeCite analysis updated for "${result.title}".`,
        detail: result.analysisMessage || null,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'EdgeCite analysis failed.';
      updateCreatedDocument(doc.documentId, {
        analysisStatus: 'failed',
        analysisMessage: message,
        errorMessage: message,
      });
      setIngestStatus({
        phase: 'error',
        message: 'EdgeCite analysis failed.',
        detail: message,
      });
    } finally {
      setLoading(false);
    }
  }, [runEdgeCiteUntilReady, updateCreatedDocument]);

  const handleAskAiAboutDocument = useCallback(() => {
    if (!canLaunchAi || !latestCreatedDocument) return;
    const payload: PendingUploadDeepSearchHandoff = {
      provider: 'edgecite_weaviate',
      matterId: formData.matterId,
      documentId: latestCreatedDocument.documentId,
      versionId: latestCreatedDocument.versionId,
      documentTitle: latestCreatedDocument.title,
      jobId: latestCreatedDocument.jobId || null,
      chunkCount: latestCreatedDocument.chunkCount ?? null,
      citationCount: latestCreatedDocument.citationCount ?? null,
      clauseCount: latestCreatedDocument.clauseCount ?? null,
      partyCount: latestCreatedDocument.partyCount ?? null,
      prompt: buildDefaultUploadedDocumentPrompt(latestCreatedDocument.title),
      createdAt: new Date().toISOString(),
    };
    localStorage.setItem(PENDING_UPLOAD_DEEP_SEARCH_KEY, JSON.stringify(payload));
    onClose();
    navigate('/chat');
  }, [canLaunchAi, formData.matterId, latestCreatedDocument, navigate, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const modal = (
    <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm lex-overlay-enter">
      <div className="relative flex h-[calc(100vh-2rem)] max-h-[960px] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900 lex-modal-enter">
        
        {/* Enhanced Header */}
        <div className="bg-lex-accent px-6 py-4 text-lex-accent-contrast flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <DocumentTextIcon className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Add Document</h2>
                <p className="text-lex-accent-contrast/80 text-sm mt-1">Create new documents or upload existing files</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-6">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              
              {/* Left Column - Document Metadata */}
              <div className="space-y-6">
                <EnhancedDocumentMetadataForm
                  formData={formData}
                  onInputChange={handleInputChange}
                  mode="upload"
                  realmId={realmId}
                  uploadedFiles={uploadedFiles}
                  onAutofillTitleAndDescription={processWithAI}
                  isAutofillingTitleAndDescription={aiProcessing}
                  canAutofillTitleAndDescription={canRunAiPrefill}
                  initialClientName={initialClientName}
                  initialMatterName={initialMatterName}
                  initialMatterNumber={initialMatterNumber}
                />
              </div>

              {/* Right Column - File Upload & AI Suggestions */}
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-6 border border-green-200 dark:border-green-800">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <CloudArrowUpIcon className="h-5 w-5 text-green-600" />
                      File Upload
                    </h3>
                    <div className="text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900 px-2 py-1 rounded-full">
                      Max 50MB
                    </div>
                  </div>

                  <FileUploadArea
                    onFilesUploaded={handleFileUpload}
                    uploadedFiles={uploadedFiles}
                    realmId={realmId}
                  />

                  {(aiPrefillNotice || aiSuggestions || ingestStatus.phase !== 'idle' || createdDocuments.length > 0) && (
                    <div className="mt-6 rounded-xl border border-emerald-200 bg-white/70 p-5 dark:border-emerald-900/60 dark:bg-gray-900/40">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h4 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            {ingestStatus.phase === 'error' ? (
                              <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                            ) : ingestStatus.phase === 'analyzing' || loading ? (
                              <ArrowPathIcon className="h-5 w-5 animate-spin text-lex-accent" />
                            ) : (
                              <LightBulbIcon className="h-5 w-5 text-lex-accent" />
                            )}
                            Ingestion Status
                          </h4>
                          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                            {ingestStatus.message}
                          </p>
                          {ingestStatus.detail && (
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {ingestStatus.detail}
                            </p>
                          )}
                        </div>
                        <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                          {stageLabel}
                        </span>
                      </div>

                      {aiPrefillNotice && (
                        <div className="mt-4 rounded-lg border border-lex-accent/20 bg-lex-accent-soft dark:bg-lex-accent/15 px-3 py-2 text-sm text-lex-accent dark:text-lex-accent-contrast">
                          {aiPrefillNotice}
                        </div>
                      )}

                      {renderingGuarantee && (
                        <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-200">
                          {renderingGuarantee}
                        </div>
                      )}

                      {aiSuggestions && (
                        <div className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                AI read the uploaded document
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Title confidence {Math.round(aiSuggestions.confidence.title * 100)}% · Description confidence {Math.round(aiSuggestions.confidence.description * 100)}%
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={applyAiSuggestions}
                              className="rounded-lg border border-lex-accent px-3 py-1.5 text-xs font-semibold text-lex-accent dark:text-lex-accent-contrast transition-colors hover:bg-lex-accent-soft dark:hover:bg-lex-accent/20"
                            >
                              Re-apply
                            </button>
                          </div>
                        </div>
                      )}

                      {createdDocuments.length > 0 && (
                        <div className="mt-4 space-y-3">
                          {createdDocuments.map((doc) => (
                            <div
                              key={`${doc.documentId}:${doc.versionId}`}
                              className="rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                    {doc.title}
                                  </p>
                                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    {doc.analysisMessage || 'EdgeCite is preparing analysis status.'}
                                  </p>
                                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                                    <div
                                      className={`h-full rounded-full transition-all ${
                                        doc.analysisStatus === 'failed'
                                          ? 'bg-red-500'
                                          : doc.analysisStatus === 'completed'
                                            ? 'bg-emerald-500'
                                            : 'bg-lex-accent'
                                      }`}
                                      style={{ width: `${Math.max(4, Math.min(100, doc.analysisProgress || 0))}%` }}
                                    />
                                  </div>
                                  <div className="mt-3 flex flex-wrap gap-2">
                                    {doc.chunkCount != null && (
                                      <span className="rounded-full bg-gray-100 px-2 py-1 text-[11px] font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                                        {doc.chunkCount} chunks
                                      </span>
                                    )}
                                    {doc.citationCount != null && (
                                      <span className="rounded-full bg-blue-50 px-2 py-1 text-[11px] font-medium text-blue-700 dark:bg-blue-950/40 dark:text-blue-200">
                                        {doc.citationCount} citations
                                      </span>
                                    )}
                                    {doc.clauseCount != null && (
                                      <span className="rounded-full bg-purple-50 px-2 py-1 text-[11px] font-medium text-purple-700 dark:bg-purple-950/40 dark:text-purple-200">
                                        {doc.clauseCount} clauses
                                      </span>
                                    )}
                                    {doc.partyCount != null && (
                                      <span className="rounded-full bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
                                        {doc.partyCount} parties
                                      </span>
                                    )}
                                    {doc.embeddingModel && (
                                      <span className="rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200">
                                        {doc.embeddingModel}
                                      </span>
                                    )}
                                  </div>
                                  {doc.feed && doc.feed.length > 0 && (
                                    <div className="mt-3 space-y-1">
                                      {doc.feed.slice(0, 3).map((item, index) => (
                                        <div key={`${item.timestamp}:${item.label}:${index}`} className="flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                                          <span className={`h-1.5 w-1.5 rounded-full ${
                                            item.status === 'completed'
                                              ? 'bg-emerald-500'
                                              : item.status === 'failed'
                                                ? 'bg-red-500'
                                                : 'bg-lex-accent'
                                          }`} />
                                          <span>{item.label}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="flex shrink-0 flex-col items-end gap-2">
                                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                                  doc.analysisStatus === 'completed'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                    : doc.analysisStatus === 'failed'
                                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                                }`}>
                                    {doc.analysisStatus.replace('_', ' ')}
                                  </span>
                                  {(doc.analysisStatus === 'failed' || doc.analysisStatus === 'timed_out' || doc.analysisStatus === 'completed') && (
                                    <button
                                      type="button"
                                      onClick={() => handleRerunEdgeCiteAnalysis(doc)}
                                      disabled={loading}
                                      className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-[11px] font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                                    >
                                      <ArrowPathIcon className="h-3.5 w-3.5" />
                                      {doc.analysisStatus === 'completed' ? 'Re-run' : 'Retry'}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {canLaunchAi && (
                        <button
                          type="button"
                          onClick={handleAskAiAboutDocument}
                          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-lex-accent px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-lex-accent-hover"
                        >
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                          Ask AI About This Document
                        </button>
                      )}
                    </div>
                  )}

                  {/* Document Version Information */}
                  <div className="mt-6 space-y-4">
                    <h4 className="text-md font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <TagIcon className="h-4 w-4 text-lex-accent" />
                      Document Version Information
                    </h4>
                    
                    {/* Initial Version */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Initial Version
                      </label>
                      <input
                        type="text"
                        value={formData.version}
                        onChange={(e) => handleInputChange('version', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                        placeholder="1.0"
                      />
                    </div>

                    {/* Document Status */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Document Status
                      </label>
                      <select
                        value={formData.lifecycleStage}
                        onChange={(e) => handleInputChange('lifecycleStage', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                      >
                        <option value="draft">Draft</option>
                        <option value="review">Under Review</option>
                        <option value="final">Final</option>
                        <option value="approved">Approved</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>

                    {/* Author */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Author
                      </label>
                      <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white">
                        <UserIcon className="h-4 w-4 text-gray-500" />
                        <span className="text-sm">
                          {currentUser?.name || 'Loading user...'}
                        </span>
                        {currentUser && (
                          <CheckCircleIcon className="h-4 w-4 text-green-500 ml-auto" />
                        )}
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

          </div>

          {/* Form Actions */}
          <div className="flex flex-shrink-0 gap-4 border-t border-gray-200 bg-white px-6 py-4 dark:border-gray-700 dark:bg-gray-900">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !canSubmitMatterScopedUpload}
                className="flex-2 px-8 py-3 bg-lex-accent hover:bg-lex-accent-hover text-white rounded-lg font-medium transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <DocumentTextIcon className="h-5 w-5" />
                    Add Document
                  </>
                )}
              </button>
            </div>
          </form>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default AddDocumentModal; 
